/**
 * What TallyPrime said, as stored beside the book (migration 0036), read as
 * the person signed in: one account's bills, receipts and Timely allocations
 * (`source_account_detail`, for whoever may see the account); the company's
 * facts behind the receivables position (`source_receivables_facts`); and the
 * accounting checks (`source_findings_list`). Every function checks the
 * caller's right and their view of each account in the database.
 *
 * A database without 0036 has none of these functions; every hook then says
 * `available: false` and the screens show nothing extra — never an error for
 * a feature that is not there yet.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
    AllocationLine, AllocationStatus, CheckCode, CheckSeverity, Finding, SourceAccountFact, SourceBillFact, SourceFacts,
} from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { userFacingRefusal } from '../lib/refusal';
import { isMissingFunction } from './useReceivablesSettings';

export interface StoredEvent {
    eventKey: string;
    kind: 'credit' | 'allocation_change' | 'reversal' | 'voucher_changed';
    evidence: 'voucher' | 'movement';
    status: AllocationStatus | null;
    amount: number;
    effectiveDate: string;
    supersedes: string | null;
    detectedAt: string;
    detail: {
        allocations?: AllocationLine[];
        unallocated?: { kind: string; ref?: string; amount: number }[];
        explanation?: string;
        observed?: { ref: string; change: number }[];
        voucher?: { number?: string; type?: string; date?: string; postDated?: boolean; instrument?: { number?: string } };
        bills?: { ref: string; amount: number }[];
        change?: string;
    };
}

export interface StoredAllocation {
    id: string;
    creditEventId: string | null;
    lines: { billKey: string; ref: string; amount: number }[];
    total: number;
    reason: string;
    state: 'active' | 'suspended' | 'superseded' | 'withdrawn';
    stateReason: string | null;
    comparison: { tally: AllocationLine[]; sameAsTimely: boolean } | null;
    createdAt: string;
    createdBy: string | null;
    stateChangedAt: string | null;
}

export interface AccountDetail {
    facts: (Pick<SourceAccountFact, 'sourceKey' | 'group' | 'billWise' | 'closing' | 'unbilled' | 'bills'> & { asOf: string; capturedAt: string; closedBills: { key: string; ref: string; billDate: string; amount: number; closedAt: string }[] }) | null;
    events: StoredEvent[];
    allocations: StoredAllocation[];
    canAllocate: boolean;
}

interface Loaded<T> { loading: boolean; available: boolean; data: T | null; error: string | null }

function useRpc<T>(fn: string, args: Record<string, unknown>, key: string): Loaded<T> & { reload: () => Promise<void> } {
    const [state, setState] = useState<Loaded<T>>({ loading: true, available: true, data: null, error: null });
    const issued = useRef(key);
    const reload = useCallback(async () => {
        issued.current = key;
        const { data, error } = await supabase().rpc(fn, args);
        if (issued.current !== key) return;
        if (error) setState({ loading: false, available: !isMissingFunction(error), data: null, error: isMissingFunction(error) ? null : userFacingRefusal(error, 'This could not be read just now.') });
        else setState({ loading: false, available: true, data: data as T, error: null });
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setState(s => ({ ...s, loading: true })); void reload(); }, [reload]);
    return { ...state, reload };
}

/** One account's stored facts, receipts and Timely allocations. */
export const useAccountDetail = (workspaceId: string, customerId: string) =>
    useRpc<AccountDetail>('source_account_detail', { p_workspace: workspaceId, p_customer: customerId }, `${workspaceId}/${customerId}`);

export interface CompanyFactsRow { sourceKey: string; customerId: string; name: string; group: string; billWise: boolean; closing: number; unbilled: number; bills: SourceBillFact[] }
export interface CompanyFacts { asOf: string | null; capturedAt: string | null; company: { id: string; name: string } | null; accounts: CompanyFactsRow[] }

export const useCompanyFacts = (workspaceId: string) =>
    useRpc<CompanyFacts>('source_receivables_facts', { p_workspace: workspaceId }, workspaceId);

/** Stored facts, as the domain's `SourceFacts` (the interpretation runs here, with the same code as the server). */
export function toSourceFacts(f: CompanyFacts): Pick<SourceFacts, 'asOf' | 'accounts' | 'unmatchedBills'> | null {
    if (!f.asOf) return null;
    return {
        asOf: f.asOf,
        unmatchedBills: [],
        accounts: f.accounts.map(a => ({
            sourceKey: a.sourceKey, ledgerGuid: a.sourceKey.replace(/^tally:/, ''), name: a.name, group: a.group, billWise: a.billWise,
            closing: Number(a.closing), unbilled: Number(a.unbilled), bills: a.bills.map(b => ({ ...b, pending: Number(b.pending) })),
            unbilledReason: Number(a.unbilled) === 0 ? null : a.billWise ? 'on_account' : 'not_bill_wise',
        })),
    };
}

export interface StoredFinding {
    id: string;
    fingerprint: string;
    code: CheckCode;
    severity: CheckSeverity;
    customerId: string | null;
    account: string | null;
    sourceKey: string | null;
    facts: Finding['facts'];
    firstSeenAt: string;
    lastSeenAt: string;
    resolvedAt: string | null;
    acknowledgedAt: string | null;
    acknowledgedBy: string | null;
    ackNote: string | null;
}

export const useFindings = (workspaceId: string, includeResolved: boolean) =>
    useRpc<StoredFinding[]>('source_findings_list', { p_workspace: workspaceId, p_include_resolved: includeResolved }, `${workspaceId}/${includeResolved}`);

/** A stored finding, as the domain's `Finding` (for its words). */
export const asFinding = (f: StoredFinding): Finding => ({
    fingerprint: f.fingerprint, code: f.code, severity: f.severity,
    ...(f.sourceKey ? { sourceKey: f.sourceKey } : {}), ...(f.account ? { account: f.account } : {}), facts: f.facts ?? {},
});

async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase().rpc(fn, args);
    if (error) throw new Error(userFacingRefusal(error));
    return data as T;
}

export const createAllocation = (workspaceId: string, customerId: string, creditEvent: string | null, lines: { billKey: string; amount: number }[], reason: string) =>
    call<string>('operational_allocation_create', { p_workspace: workspaceId, p_customer: customerId, p_credit_event: creditEvent, p_lines: lines, p_reason: reason });

export const withdrawAllocation = (workspaceId: string, allocationId: string, reason: string | null) =>
    call<void>('operational_allocation_withdraw', { p_workspace: workspaceId, p_allocation: allocationId, p_reason: reason });

export const acknowledgeFinding = (workspaceId: string, findingId: string, note: string | null) =>
    call<void>('source_finding_acknowledge', { p_workspace: workspaceId, p_finding: findingId, p_note: note });

export const unacknowledgeFinding = (workspaceId: string, findingId: string) =>
    call<void>('source_finding_unacknowledge', { p_workspace: workspaceId, p_finding: findingId });
